import { ListItemButton } from '@mui/material';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { dispatchSlice } from '@api/Trinium/Endpoints/Dispatch/slice';
import { LegStatus } from '../../../../Api/Trinium/types';
import { getEnumValueByKey } from '../../../../Infrastructure/Utilities';
import { PutLegRequest, batchLegRequest } from '@api/Trinium/Endpoints/Leg';
import { batchLegLabel } from '@components/DataGrid/Dispatch/types';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { store } from '@reduxStore';
import { useMemo } from 'react';

/** Statuses a leg cannot be overridden from (already On Site, Picked Up, or Delivered) */
const ineligibleStatuses = [LegStatus.OS, LegStatus.PU, LegStatus.DL];

const legLabel = (leg: DispatchLeg) => `${String(leg.jobId)}/${leg.csnNo.toString().padStart(3, '0')} leg ${String(leg.legNo)}`;

/**
 * Set On Site leg status for the selected leg(s).
 * A leg is eligible when it is not On Site, Picked Up, or Delivered and,
 * if it is not the first leg of its consignment, the previous leg is Delivered.
 */
export default function SetOnSiteAction() {
	const gridApiRef = useGridApiContext();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);
	const { number: terminalNumber } = useCurrentTerminal();

	const candidateLegs = useMemo(
		() =>
			Array.from(selectedRowIds.keys())
				.map((legId) => gridApiRef.current.getRow<DispatchLeg>(legId))
				.filter(
					(leg): leg is DispatchLeg =>
						!!leg && !ineligibleStatuses.includes(getEnumValueByKey(LegStatus, leg.legStatus) as LegStatus)
				),
		[gridApiRef, selectedRowIds]
	);

	if (candidateLegs.length === 0) {
		return <></>;
	}

	/** Fetch the legs of each selected job (cached per job) to validate the previous leg is Delivered */
	const getEligibleLegs = async (): Promise<DispatchLeg[]> => {
		const jobIds = [...new Set(candidateLegs.map((leg) => leg.jobId))];
		const jobLegs = (
			await Promise.all(
				jobIds.map((jobId) =>
					store
						.dispatch(
							dispatchSlice.endpoints.fetchDispatchLegsByJobId.initiate({
								terminalNumber,
								jobId: String(jobId)
							})
						)
						.unwrap()
				)
			)
		).flat();

		return candidateLegs.filter((leg) => {
			const previousLeg = jobLegs
				.filter((jobLeg) => jobLeg.csnId === leg.csnId && jobLeg.legNo < leg.legNo)
				.reduce<DispatchLeg | undefined>((prev, jobLeg) => (!prev || jobLeg.legNo > prev.legNo ? jobLeg : prev), undefined);

			if (previousLeg && getEnumValueByKey(LegStatus, previousLeg.legStatus) !== LegStatus.DL) {
				store.dispatch(
					appendErrorAnnouncement({
						message: `${legLabel(leg)} was not set On Site: the previous leg must be Delivered`
					})
				);
				return false;
			}
			return true;
		});
	};

	const handleBtnClick = () => {
		getEligibleLegs()
			.then((eligibleLegs) => {
				if (eligibleLegs.length === 0) return;

				const requests: Omit<PutLegRequest, 'terminalNumber'>[] = eligibleLegs.map((leg) => ({
					id: leg.legId,
					actionType: {
						action: 'setOnSite'
					}
				}));

				void batchLegRequest(requests);
			})
			.catch((error: unknown) => {
				store.dispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
			});
	};

	return <ListItemButton onClick={handleBtnClick}>{batchLegLabel('Override to On Site Status', candidateLegs.length)}</ListItemButton>;
}
